import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { SubTask, Task, TaskTypeAction } from '@k12/models';

interface AwardEndorsementFormData {
  householdId?: string | null;
  accountId?: string | null;
  programPartnerId?: string | null;
  academicYearId?: number | string | null;
  studentId?: string | null;
  studentName?: string | null;
  schoolName?: string | null;
  programTypeId?: number | string | null;
  programType?: string | null;
  semester?: string | null;
  awardAmount?: number | string | null;
  certified?: boolean | null;
  accepted?: boolean | null;
}

@Component({
  selector: 'lib-award-endorsement-task-form',
  standalone: true,
  imports: [
    CurrencyPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatInputModule
  ],
  templateUrl: './award-endorsement.component.html',
  styleUrl: './award-endorsement.component.scss'
})
export class AwardEndorsementTaskFormComponent implements OnInit {
  @Input() task: Task | null = null;
  @Input() taskAction!: SubTask | null;
  @Input() taskTypeAction: TaskTypeAction | null = null;
  @Input() isReadOnly: boolean = false;

  @Output() submitFormData = new EventEmitter<string>();

  private readonly fb = inject(FormBuilder);

  protected form!: FormGroup;
  protected context: AwardEndorsementFormData = {};

    protected get semesterLabel(): string {
    return this.context.semester?.trim() || 'Award';
  }

  protected get programTypeLabel(): string {
    return this.context.programType?.trim() || 'Program';
  }

  protected get acknowledgementTitle(): string {
    return `${this.semesterLabel} ${this.programTypeLabel} Award Acknowledgement`;
  }

  protected get awardAmountLabel(): string {
    return `${this.semesterLabel} ${this.programTypeLabel} Award Amount`;
  }

  protected get acknowledgementText(): string {
    return `I acknowledge the ${this.semesterLabel} ${this.programTypeLabel} award amount being disbursed to the school.`;
  }

  protected get controlIdPrefix(): string {
    const semester = this.semesterLabel.toLowerCase().replaceAll(' ', '-');
    const programType = this.programTypeLabel.toLowerCase().replaceAll(' ', '-');

    return `${semester}-${programType}-award`;
  }

  public ngOnInit(): void {
    const raw = this.taskAction?.formData ? this.safeParseJson(this.taskAction.formData) : {};

    this.context = this.normalizeKeys(raw) as AwardEndorsementFormData;

    this.initializeForm();
    if (this.isReadOnly) {
      this.form.disable({ emitEvent: false });
    }
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty;
  }

  protected onBack(): void {
    globalThis.history.back();
  }

  protected submitForm(): void {
    this.form.markAllAsTouched();

    if (!this.isFormReady()) return;

    this.submitFormData.emit(
      JSON.stringify({
        ...this.context,
        ...this.form.getRawValue(),
        accepted: true
      })
    );
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      householdId: [this.context.householdId ?? null],
      accountId: [this.context.accountId ?? null],
      programPartnerId: [this.context.programPartnerId ?? null],
      academicYearId: [this.toNumberOrNull(this.context.academicYearId)],
      studentId: [this.context.studentId ?? null],

        studentName: [{ value: this.context.studentName ?? '', disabled: true }],
        schoolName: [{ value: this.context.schoolName ?? '', disabled: true }],
        awardAmount: [
        {
            value: this.toNumberOrNull(this.context.awardAmount),
            disabled: true
        }
      ],

      certified: [this.context.certified ?? false, Validators.requiredTrue]
    });
  }

  private safeParseJson(raw: string): Record<string, unknown> {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private normalizeKeys(raw: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(raw).map(([key, value]) => [key.charAt(0).toLowerCase() + key.slice(1), value])
    );
  }

  private toNumberOrNull(value?: number | string | null): number | null {
    if (value === null || value === undefined || value === '') return null;

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
